import { Injectable } from '@angular/core';
import { HttpRequest, HttpResponse, HttpHeaders, HttpParams, HttpClient } from '@angular/common/http';

import { Observable, of, delay } from 'rxjs';
import { map, filter, switchMap, catchError } from 'rxjs/operators';

import { AppContext, IService } from '../Shared/common';
import { Global } from '../Shared/global';

@Injectable()
export class CommonDataService implements IService {
  headers: HttpHeaders;
  options: {};

  constructor(private _http: HttpClient, private appctx: AppContext) {
    this.initializeHeaders();
  }

  /**
   * Initialize headers with safe null checks
   */
  private initializeHeaders(): void {
    const orgId = this.getOrgId();
    const unitId = this.getUnitId();
    const userId = this.getUserId();

    this.headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'q=0.8;application/json;q=0.9',
      'OrgId': orgId,
      'UnitId': unitId,
      'UserId': userId
    });
    this.options = { headers: this.headers };
  }

  /**
   * Safely get OrgId from appctx
   */
  private getOrgId(): string {
    try {
      if (this.appctx?.CurrentOrg?.OrgId) {
        return this.appctx.CurrentOrg.OrgId.toString();
      }
    } catch (e) {
      console.warn('Error getting OrgId:', e);
    }
    return '';
  }

  /**
   * Safely get UnitId from appctx
   */
  private getUnitId(): string {
    try {
      if (this.appctx?.CurrentOrg?.OrgUnits) {
        const selectedUnit = this.appctx.CurrentOrg.OrgUnits.find(x => x.IsSelected);
        if (selectedUnit?.UnitId) {
          return selectedUnit.UnitId.toString();
        }
      }
    } catch (e) {
      console.warn('Error getting UnitId:', e);
    }
    return '';
  }

  /**
   * Safely get UserId from appctx
   */
  private getUserId(): string {
    try {
      if (this.appctx?.CurrentUser?.ID) {
        return this.appctx.CurrentUser.ID.toString();
      }
    } catch (e) {
      console.warn('Error getting UserId:', e);
    }
    return '';
  }

  get(url: string): Observable<any> {
    return this._http.get(url).pipe(
      map((response: Response) => alert(response)),
      catchError(<T>(error: any, result?: T) => {
        console.log(error);
        return typeof (result as T);
      })
    );
  }

  getData(url: string): Promise<any> {
    console.log('getData URL:', url);
    this.initializeHeaders(); // Refresh headers for each request

    return this._http
      .get(url, this.options)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  getDownloadData(url: string): Promise<any> {
    console.log('getDownloadData URL:', url);
    this.initializeHeaders(); // Refresh headers for each request

    return this._http
      .get(url, this.options)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  DocumentsDownload(url: string) {
    const orgId = this.getOrgId();
    const unitId = this.getUnitId();
    const userId = this.getUserId();

    return this._http.get(url, {
      reportProgress: true,
      observe: 'events',
      responseType: 'blob',
      headers: new HttpHeaders({
        'OrgId': orgId,
        'UnitId': unitId,
        'UserId': userId
      })
    })
      .toPromise()
      .then(this.extractBlobData)
      .catch(this.handleError);
  }

  post(url: string, model: any): Observable<any> {
    let body = model;
    const orgId = this.getOrgId();
    const unitId = this.getUnitId();
    const userId = this.getUserId();

    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'OrgId': orgId,
      'UnitId': unitId,
      'UserId': userId
    });
    let options = { headers: headers };

    return this._http.post(url, body, options)
      .pipe(
        map((response: Response) => response),
        catchError(<T>(error: any, result?: T) => {
          console.log(error);
          return typeof (result as T);
        }));
  }

  put(url: string, id: number, model: any): Observable<any> {
    let body = model;
    const orgId = this.getOrgId();
    const unitId = this.getUnitId();
    const userId = this.getUserId();

    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'OrgId': orgId,
      'UnitId': unitId,
      'UserId': userId
    });
    let options = { headers: headers };

    return this._http.put(url + id, body, options)
      .pipe(
        map((response: Response) => response),
        catchError(<T>(error: any, result?: T) => {
          console.log(error);
          return typeof (result as T);
        }));
  }

  delete(url: string, id: number): Observable<any> {
    const orgId = this.getOrgId();
    const unitId = this.getUnitId();
    const userId = this.getUserId();

    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'OrgId': orgId,
      'UnitId': unitId,
      'UserId': userId
    });
    let options = { headers: headers };

    return this._http.delete(url + id, options)
      .pipe(
        map((response: Response) => response),
        catchError(<T>(error: any, result?: T) => {
          console.log(error);
          return typeof (result as T);
        }));
  }

  private extractData(res: Response) {
    let body = res;
    return body || {};
  }

  private extractBlobData(res: any) {
    let body = res;
    return body || {};
  }

  private handleError(error: any): Promise<any> {
    console.error('An error occurred', error);
    return Promise.reject(error.message || error);
  }
}
